import React, { useState, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Stack,
  Alert,
} from '@mui/material';
import {
  EVENT_GUEST_RELATIONSHIPS,
  EVENT_GUEST_RELATIONSHIP_LABELS,
  type EventGuest,
  type EventGuestRelationship,
} from '../../types/eventGuest';
import { createGuest, updateGuest } from '../../services/guestsApi';

interface GuestFormProps {
  mode: 'create' | 'edit';
  familyId: string;
  initialValue?: EventGuest;
  onSuccess: (guest: EventGuest, warning?: { message: string; existingGuestId: string }) => void;
  onCancel: () => void;
}

/**
 * Plan 4.4 — Shared create/edit form for EventGuest.
 *
 * Create mode requires the consent checkbox; edit mode hides it
 * (consent was confirmed at creation time and lives on the row).
 * `relationshipOther` is required only when `relationship === 'Other'`.
 */
const GuestForm: React.FC<GuestFormProps> = ({
  mode,
  familyId,
  initialValue,
  onSuccess,
  onCancel,
}) => {
  const [firstName, setFirstName] = useState(initialValue?.firstName ?? '');
  const [lastName, setLastName] = useState(initialValue?.lastName ?? '');
  const [phone, setPhone] = useState(initialValue?.phone ?? '');
  const [email, setEmail] = useState(initialValue?.email ?? '');
  const [relationship, setRelationship] = useState<EventGuestRelationship>(
    initialValue?.relationship ?? 'FamilyFriend',
  );
  const [relationshipOther, setRelationshipOther] = useState(
    initialValue?.relationshipOther ?? '',
  );
  // C7 — persistent per-guest dietary requirement.
  const [dietaryRequirements, setDietaryRequirements] = useState(
    initialValue?.dietaryRequirements ?? '',
  );
  const [consentConfirmed, setConsentConfirmed] = useState(mode === 'edit');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const consentCopy = useMemo(() => {
    const name = `${firstName} ${lastName}`.trim() || 'this person';
    return `I confirm ${name} is happy for me to share these details with Team Jak for event registration and emergency contact.`;
  }, [firstName, lastName]);

  const emailLooksValid = email === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const canSubmit =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    emailLooksValid &&
    (relationship !== 'Other' || relationshipOther.trim().length > 0) &&
    (mode === 'edit' || consentConfirmed) &&
    !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    if (!canSubmit) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'create') {
        const res = await createGuest(familyId, {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phone: phone.trim() || null,
          email: email.trim() || null,
          relationship,
          relationshipOther: relationship === 'Other' ? relationshipOther.trim() : null,
          dietaryRequirements: dietaryRequirements.trim() || null,
          consentConfirmed: true,
        });
        onSuccess(
          res.guest,
          res.warning && res.existingGuestId
            ? { message: res.warning, existingGuestId: res.existingGuestId }
            : undefined,
        );
      } else {
        if (!initialValue) {
          setError('Cannot edit: missing initial guest');
          return;
        }
        const updated = await updateGuest(familyId, initialValue.id, {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phone: phone.trim() || null,
          email: email.trim() || null,
          relationship,
          relationshipOther: relationship === 'Other' ? relationshipOther.trim() : null,
          dietaryRequirements: dietaryRequirements.trim() || null,
        });
        onSuccess(updated);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save guest');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          label="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
          autoFocus
          inputProps={{ maxLength: 80 }}
        />
        <TextField
          label="Last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          required
          inputProps={{ maxLength: 80 }}
        />
        <TextField
          label="Phone (optional)"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          inputProps={{ maxLength: 50 }}
        />
        <TextField
          label="Email (optional)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          type="email"
          error={!emailLooksValid}
          helperText={!emailLooksValid ? 'Enter a valid email address' : ''}
          inputProps={{ maxLength: 255 }}
        />

        <FormControl required>
          <InputLabel id="guest-relationship-label">Relationship</InputLabel>
          <Select
            labelId="guest-relationship-label"
            label="Relationship"
            value={relationship}
            onChange={(e) => setRelationship(e.target.value as EventGuestRelationship)}
          >
            {EVENT_GUEST_RELATIONSHIPS.map((r) => (
              <MenuItem key={r} value={r}>
                {EVENT_GUEST_RELATIONSHIP_LABELS[r]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {relationship === 'Other' && (
          <TextField
            label="Specify relationship"
            value={relationshipOther}
            onChange={(e) => setRelationshipOther(e.target.value)}
            required
            inputProps={{ maxLength: 50 }}
          />
        )}

        <TextField
          label="Dietary requirements (optional)"
          placeholder="e.g. nut allergy, gluten free"
          value={dietaryRequirements}
          onChange={(e) => setDietaryRequirements(e.target.value)}
          inputProps={{ maxLength: 500 }}
        />

        {mode === 'create' && (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={consentConfirmed}
                  onChange={(e) => setConsentConfirmed(e.target.checked)}
                />
              }
              label={consentCopy}
            />
            <FormHelperText sx={{ ml: 4 }}>
              See our{' '}
              <Link href="/support" target="_blank" rel="noopener">
                Privacy Policy
              </Link>{' '}
              for details on how we use this information.
            </FormHelperText>
          </Box>
        )}

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1 }}>
          <Button onClick={onCancel} color="inherit" disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit}
          >
            {mode === 'create' ? 'Add guest' : 'Save changes'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default GuestForm;
